import type { BrandArtifactOrigin } from './attribution';

export type BrandArtifactDisposition =
  | 'CANDIDATE_ADAPTER'
  | 'LEGACY_DESTINATION'
  | 'TRANSPORT_ONLY';

export interface BrandArtifactRegistration {
  readonly id: string;
  readonly channel: 'email' | 'html_document' | 'pdf' | 'browser_print' | 'transport';
  readonly origin: BrandArtifactOrigin | null;
  readonly disposition: BrandArtifactDisposition;
  readonly producerPath: string;
  readonly adapterPath: string | null;
  readonly candidateCoverageEligible: boolean;
  readonly legacyReason: string | null;
}

export const BRAND_ARTIFACT_REGISTRY = [
  {
    id: 'templated_tenant_email',
    channel: 'email',
    origin: 'tenant_originated',
    disposition: 'CANDIDATE_ADAPTER',
    producerPath: 'apps/web/src/lib/email/send.ts',
    adapterPath: 'apps/web/src/lib/email/brand-adapter.ts',
    candidateCoverageEligible: true,
    legacyReason: null,
  },
  {
    id: 'templated_system_email',
    channel: 'email',
    origin: 'statura_system',
    disposition: 'CANDIDATE_ADAPTER',
    producerPath: 'apps/web/src/lib/email/send.ts',
    adapterPath: 'apps/web/src/lib/email/brand-adapter.ts',
    candidateCoverageEligible: true,
    legacyReason: null,
  },
  {
    id: 'dashboard_report_email',
    channel: 'email',
    origin: 'tenant_originated',
    disposition: 'CANDIDATE_ADAPTER',
    producerPath: 'apps/web/src/app/api/cron/dispatch-dashboard-reports/route.ts',
    adapterPath: 'packages/email/src/layout.ts',
    candidateCoverageEligible: true,
    legacyReason: null,
  },
  {
    id: 'report_subscription_email',
    channel: 'email',
    origin: 'tenant_originated',
    disposition: 'CANDIDATE_ADAPTER',
    producerPath: 'apps/web/src/app/api/cron/dispatch-report-subscriptions/route.ts',
    adapterPath: 'packages/email/src/layout.ts',
    candidateCoverageEligible: true,
    legacyReason: null,
  },
  {
    id: 'signature_document_request_email',
    channel: 'email',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/signing/document-template-actions.ts',
    adapterPath: 'packages/email/src/layout.ts',
    candidateCoverageEligible: false,
    legacyReason:
      'Dedicated external-signer delivery remains on its certified legacy composition until its token/public-signing evidence packet is registered.',
  },
  {
    id: 'portal_notification_email',
    channel: 'email',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/client-portal/src/lib/portal-session.ts',
    adapterPath: 'packages/email/src/layout.ts',
    candidateCoverageEligible: false,
    legacyReason:
      'Portal-triggered staff notification remains the canonical Statura legacy shell until portal email candidate evidence is added.',
  },
  {
    id: 'email_template_admin_preview',
    channel: 'email',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/(admin)/email-preview/page.tsx',
    adapterPath: 'packages/email/src/layout.ts',
    candidateCoverageEligible: false,
    legacyReason: 'Internal template preview deliberately renders the certified legacy example.',
  },
  {
    id: 'sah_monthly_statement_email',
    channel: 'email',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/(dashboard)/home-care/sah-drawdown.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Regulated SAH statement HTML and envelope remain an indivisible certified legacy artifact family until statement-specific brand evidence closes.',
  },
  {
    id: 'notification_heartbeat_emergency_email',
    channel: 'email',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/api/cron/notification-heartbeat/route.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Emergency plain-text alert deliberately avoids tenant branding and application dependencies.',
  },
  {
    id: 'system_health_emergency_email',
    channel: 'email',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/api/cron/system-health/route.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Emergency database-outage alert deliberately avoids tenant branding and database-backed adapters.',
  },
  {
    id: 'identity_recovery_email',
    channel: 'email',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/identity/recovery-email.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Identity recovery uses a minimal Statura security composition and cannot load tenant identity before authentication.',
  },
  {
    id: 'workflow_retry_email_fragment',
    channel: 'email',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/workflow-routing/retry.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Workflow retry copy is appended to an already-rendered governed email and carries no separate tenant identity.',
  },
  {
    id: 'raw_resend_html_transport',
    channel: 'transport',
    origin: null,
    disposition: 'TRANSPORT_ONLY',
    producerPath: 'apps/web/src/lib/email.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Transport accepts already-rendered HTML and cannot select artifact identity.',
  },
  {
    id: 'branded_document_html',
    channel: 'html_document',
    origin: 'tenant_originated',
    disposition: 'CANDIDATE_ADAPTER',
    producerPath: 'apps/web/src/lib/documents/branded-document.tsx',
    adapterPath: 'apps/web/src/lib/documents/branded-document.tsx',
    candidateCoverageEligible: true,
    legacyReason: null,
  },
  {
    id: 'branded_system_document_html',
    channel: 'html_document',
    origin: 'statura_system',
    disposition: 'CANDIDATE_ADAPTER',
    producerPath: 'apps/web/src/lib/documents/branded-document.tsx',
    adapterPath: 'apps/web/src/lib/documents/branded-document.tsx',
    candidateCoverageEligible: true,
    legacyReason: null,
  },
  {
    id: 'compliance_audit_pack_html',
    channel: 'html_document',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/audit/compliance-audit-pack.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Compliance audit-pack failure banners retain the regulated legacy evidentiary composition pending packet-specific renders.',
  },
  {
    id: 'unsubscribe_status_html',
    channel: 'html_document',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/api/unsubscribe/route.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Public unsubscribe status is a minimal Statura system page without authenticated tenant identity.',
  },
  {
    id: 'service_agreement_review_html',
    channel: 'html_document',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath:
      'apps/web/src/app/(dashboard)/clients/[id]/care-plans/service-agreements/[agreementId]/review/route.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Plain-text service-agreement review retains its approved legacy document view pending signing-family cutover evidence.',
  },
  {
    id: 'onboarding_evidence_image_flatten',
    channel: 'transport',
    origin: null,
    disposition: 'TRANSPORT_ONLY',
    producerPath: 'apps/web/src/lib/hr/onboarding-evidence-image-optimiser.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Image normalisation flattens transparency for OCR/storage transport and does not render customer identity.',
  },
  {
    id: 'signed_document_pdf',
    channel: 'pdf',
    origin: 'tenant_originated',
    disposition: 'CANDIDATE_ADAPTER',
    producerPath: 'apps/web/src/lib/signing/signed-document-pdf.tsx',
    adapterPath: 'apps/web/src/lib/signing/signed-document-pdf.tsx',
    candidateCoverageEligible: true,
    legacyReason: null,
  },
  {
    id: 'signed_system_document_pdf',
    channel: 'pdf',
    origin: 'statura_system',
    disposition: 'CANDIDATE_ADAPTER',
    producerPath: 'apps/web/src/lib/signing/signed-document-pdf.tsx',
    adapterPath: 'apps/web/src/lib/signing/signed-document-pdf.tsx',
    candidateCoverageEligible: true,
    legacyReason: null,
  },
  {
    id: 'case_note_period_pack_pdf',
    channel: 'pdf',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/documents/case-note-period-pack-pdf.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Clinical case-note pack retains its certified legacy print composition.',
  },
  {
    id: 'payer_invoice_pdf',
    channel: 'pdf',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/documents/payer-invoice-pdf.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Payer invoice retains its regulated legacy print composition.',
  },
  {
    id: 'sah_statement_pdf',
    channel: 'pdf',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/documents/sah-statement-pdf.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'SAH statement PDF remains paired with the legacy statement email until statement-specific brand evidence closes.',
  },
  {
    id: 'report_pdf_serializer',
    channel: 'pdf',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/reports/pdf-serializer.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Generic scheduled-report PDF bytes retain their certified Statura report composition until report-family identity evidence is registered.',
  },
  {
    id: 'training_certificate_pdf',
    channel: 'pdf',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/training/certificate-pdf.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Training certificates remain on the existing Statura certificate composition until certificate-specific rendered evidence closes.',
  },
  {
    id: 'training_transcript_pdf',
    channel: 'pdf',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/training/transcript-pdf.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Training transcripts remain on the existing Statura transcript composition until transcript-specific rendered evidence closes.',
  },
  {
    id: 'standards_evidence_pack_pdf',
    channel: 'pdf',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/audit/standards-evidence-pack.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Clinical standards evidence packs retain their regulated legacy PDF composition pending a dedicated artifact-identity packet.',
  },
  {
    id: 'care_minutes_audit_pdf',
    channel: 'pdf',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/audit/care-minutes-audit-pdf.ts',
    adapterPath: 'apps/web/src/lib/reports/pdf-serializer.ts',
    candidateCoverageEligible: false,
    legacyReason:
      'Care-minutes audit packs retain the regulated legacy report serializer until care-minutes artifact evidence closes.',
  },
  {
    id: 'grid_browser_print_pdf',
    channel: 'browser_print',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/lib/csv-export.ts',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Browser Save-as-PDF grid exports remain on their existing print stylesheet until a governed print identity adapter is implemented.',
  },
  {
    id: 'generic_page_browser_print',
    channel: 'browser_print',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/components/print-button.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Generic page print actions retain the certified legacy page stylesheet.',
  },
  {
    id: 'payroll_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/components/payroll/PrintButton.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Payroll print artifacts retain their evidentiary legacy composition.',
  },
  {
    id: 'board_pack_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/(dashboard)/analytics/board-pack/page.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Board packs remain on the governed legacy report composition.',
  },
  {
    id: 'care_plan_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/(dashboard)/care-plans/[id]/printable/PrintButton.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Care-plan browser print remains paired with the certified legacy care-plan artifact.',
  },
  {
    id: 'roster_import_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/(dashboard)/roster/import/provider-roster-import-workbench.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Roster-import evidence prints retain the existing operational stylesheet.',
  },
  {
    id: 'finance_statement_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/(dashboard)/finance/reports/statements/page.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Financial statement browser print remains on its certified legacy composition.',
  },
  {
    id: 'prudential_report_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath: 'apps/web/src/app/(dashboard)/compliance/prudential/reports/page.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason:
      'Prudential annual-statement print evidence retains its regulated legacy composition until statement-specific rendered evidence closes.',
  },
  {
    id: 'client_record_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath:
      'apps/web/src/app/(dashboard)/clients/[id]/_components/ClientRecordActionsMenu.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Client-record print actions retain the selected record legacy stylesheet.',
  },
  {
    id: 'sah_journey_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath:
      'apps/web/src/app/(dashboard)/clients/[id]/journey/printable/JourneyPrintableDocument.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'The audited SAH journey print request remains on its certified legacy document.',
  },
  {
    id: 'sirs_notification_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath:
      'apps/web/src/app/(dashboard)/compliance/incidents/[id]/sirs-notification/SirsNotificationPrintButton.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'SIRS notification print evidence retains the regulated legacy composition.',
  },
  {
    id: 'incident_record_browser_print',
    channel: 'browser_print',
    origin: 'tenant_originated',
    disposition: 'LEGACY_DESTINATION',
    producerPath:
      'apps/web/src/app/(dashboard)/compliance/incidents/[id]/print/PrintTriggerButton.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Incident record print evidence retains the regulated legacy composition.',
  },
  {
    id: 'course_certificate_browser_print',
    channel: 'browser_print',
    origin: 'statura_system',
    disposition: 'LEGACY_DESTINATION',
    producerPath:
      'apps/web/src/app/(dashboard)/me/courses/[id]/certificate/CertificatePrintButton.tsx',
    adapterPath: null,
    candidateCoverageEligible: false,
    legacyReason: 'Course certificates retain the certified Statura learning composition.',
  },
] as const satisfies readonly BrandArtifactRegistration[];

export type BrandArtifactRegistrationId = (typeof BRAND_ARTIFACT_REGISTRY)[number]['id'];
type RegisteredBrandArtifact = (typeof BRAND_ARTIFACT_REGISTRY)[number];

export function getBrandArtifactRegistration<Id extends BrandArtifactRegistrationId>(
  id: Id,
): Extract<RegisteredBrandArtifact, { id: Id }> {
  const registration = BRAND_ARTIFACT_REGISTRY.find((entry) => entry.id === id);
  if (!registration) throw new Error('brand_artifact_registration_unknown');
  return registration as Extract<RegisteredBrandArtifact, { id: Id }>;
}

export function requireBrandArtifactRegistration(
  id: BrandArtifactRegistrationId,
  expected: {
    origin: BrandArtifactOrigin;
    disposition: Exclude<BrandArtifactDisposition, 'TRANSPORT_ONLY'>;
  },
): (typeof BRAND_ARTIFACT_REGISTRY)[number] {
  const registration = getBrandArtifactRegistration(id);
  if (
    registration.origin !== expected.origin ||
    registration.disposition !== expected.disposition
  ) {
    throw new Error('brand_artifact_registration_mismatch');
  }
  return registration;
}
